import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import Tooltip from './Tooltip';

type SegmentedIcon = React.ComponentType<{
  size?: number;
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
}>;

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: SegmentedIcon;
  badge?: number;
  tooltip?: ReactNode;
  /** Overrides the default blue selected style for this option. */
  selectedClassName?: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  /** Shown on hover when the control is disabled. */
  disabledReason?: string;
  size?: 'md' | 'sm';
  selectedClassName?: string;
  /** Classes for options that are not selected. */
  unselectedClassName?: string;
  /** Slides the selected background between options instead of swapping it in place. */
  indicator?: boolean;
}

export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  disabled = false,
  disabledReason,
  size = 'md',
  selectedClassName = 'bg-primary-lightblue text-secondary-blue font-semibold',
  unselectedClassName,
  indicator = false
}: SegmentedControlProps<T>) {
  const small = size === 'sm';
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );
  const control = (
    <ToggleGroup
      disabled={disabled}
      value={value == null ? [] : [value]}
      aria-label={ariaLabel}
      className={clsx(
        'relative inline-flex items-center rounded-lg border-2 border-border-default',
        indicator && 'overflow-hidden',
        small ? 'h-7' : 'h-9',
        disabled && 'opacity-60 cursor-not-allowed',
        className
      )}
      onValueChange={(groupValue) => {
        const next = groupValue[0];
        if (next != null) onChange(next as T);
      }}
    >
      {indicator
        ? options
            .slice(1)
            .map((option, index) => (
              <span
                key={option.value}
                aria-hidden
                className="pointer-events-none absolute inset-y-0 z-0 w-px bg-border-subtle"
                style={{ left: `${((index + 1) * 100) / options.length}%` }}
              />
            ))
        : null}
      {indicator && value != null ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 z-10 bg-primary-lightblue transition-[left] duration-300 ease-out motion-reduce:transition-none"
          style={{
            width: `calc(${100 / options.length}% + 1px)`,
            left: `${(selectedIndex * 100) / options.length}%`
          }}
        />
      ) : null}
      {options.map((option, index) => {
        const selected = value === option.value;
        const Icon = option.icon;
        const inner = (
          <>
            {Icon && (
              <span className="relative">
                <Icon size={small ? 14 : 18} weight="regular" />
                {option.badge !== undefined && option.badge > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex min-w-3.5 h-3.5 items-center justify-center rounded-full bg-misc-error px-1 text-[10px] font-semibold leading-none text-white">
                    {option.badge > 99 ? '99+' : option.badge}
                  </span>
                )}
              </span>
            )}
            {option.label}
          </>
        );

        return (
          <Toggle
            key={option.value}
            value={option.value}
            disabled={disabled}
            aria-label={!option.label && typeof option.tooltip === 'string' ? option.tooltip : undefined}
            className={clsx(
              'group relative z-20 inline-flex h-full flex-1 items-center justify-center gap-1 font-medium transition-colors whitespace-nowrap first:rounded-l-md last:rounded-r-md',
              small ? 'px-2 text-xs' : 'px-3 text-sm gap-1.5',
              !indicator && index > 0 && 'border-l border-border-subtle',
              selected
                ? indicator
                  ? (option.selectedClassName ??
                    (selectedClassName === 'bg-primary-lightblue text-secondary-blue font-semibold'
                      ? 'bg-transparent font-semibold text-secondary-blue'
                      : selectedClassName))
                  : (option.selectedClassName ?? selectedClassName)
                : disabled
                  ? 'text-content-subtle'
                  : (unselectedClassName ?? 'text-content-subtle hover:bg-surface-hover'),
              disabled && 'cursor-not-allowed'
            )}
          >
            {inner}
            {option.tooltip ? (
              <Tooltip title={option.tooltip} placement="top">
                <span className="absolute inset-0" aria-hidden />
              </Tooltip>
            ) : null}
          </Toggle>
        );
      })}
    </ToggleGroup>
  );

  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement="top">
        <span className="inline-flex">{control}</span>
      </Tooltip>
    );
  }

  return control;
}
